import { useEffect, useRef, useState, type ReactNode } from 'react';
import { cn } from '../cn.js';

export interface HelpTipProps {
  /** Accessible label for the trigger. */
  label?: string;
  children: ReactNode;
  className?: string;
}

/**
 * A small "?" trigger that reveals a popover of contextual help. Click to
 * toggle; closes on outside-click or Escape. Dependency-free.
 */
export function HelpTip({ label = 'More info', children, className }: HelpTipProps) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    if (!open) return;
    function onDoc(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <span ref={ref} className={cn('relative inline-flex', className)}>
      <button
        type="button"
        aria-label={label}
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        className="inline-flex h-4 w-4 items-center justify-center rounded-full border border-border text-[10px] font-semibold leading-none text-muted-foreground hover:bg-accent hover:text-foreground"
      >
        ?
      </button>
      {open ? (
        <span
          role="tooltip"
          className="absolute left-0 top-6 z-50 w-64 max-w-[80vw] rounded-md border border-border bg-card p-3 text-xs font-normal leading-relaxed text-foreground shadow-brand"
        >
          {children}
        </span>
      ) : null}
    </span>
  );
}
